import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Mountain, Calendar, Target, Heart, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { SEOHead } from '../SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/t2zfhsiq_herosection.jpg';

export function HomePage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [crags, setCrags] = useState([]);
  const [events, setEvents] = useState([]);
  const [latestRoutes, setLatestRoutes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cragsRes, eventsRes, routesRes, projectsRes] = await Promise.all([
          axios.get(`${API}/crags`),
          axios.get(`${API}/events?limit=3`),
          axios.get(`${API}/routes/latest?limit=5`),
          axios.get(`${API}/projects?limit=3`)
        ]);
        setCrags(cragsRes.data);
        setEvents(eventsRes.data);
        setLatestRoutes(routesRes.data);
        setProjects(projectsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Caltavuturo Climbing" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="caption mb-4 block text-white/80">Sicilian Climbing Community</span>
            <h1 className="heading-xl text-white mb-6">
              {t('heroTitle')}
            </h1>
            <p className="body-lg text-white/90 mb-8 max-w-xl">
              {t('heroSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/crags">
                <Button className="btn-primary" data-testid="explore-btn">
                  {t('exploreNow')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/donations">
                <Button variant="outline" className="btn-outline bg-transparent text-white border-white hover:bg-white hover:text-black">
                  {t('supportUs')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="section-padding topo-bg" data-testid="history-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="caption mb-4 block">{t('ourHistory')}</span>
              <p className="body-lg text-gray-600 mb-6 whitespace-pre-line">
                {t('homeHistoryText')}
              </p>
              <Link to="/team/about" className="inline-flex items-center text-[#FF6D00] font-semibold hover:underline">
                {t('readMore')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/2fwl0gnc_falesia%20gazzara.jpeg"
                alt="Climbing history"
                className="w-full rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#FF6D00] text-white p-6 rounded-lg">
                <p className="font-heading text-4xl font-bold">20+</p>
                <p className="text-sm uppercase tracking-wider">Anni di storia</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="mission-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Target className="w-12 h-12 text-[#FF6D00] mx-auto mb-6" />
              <h2 className="heading-lg text-white mb-6">
                {t('missionTitle')}
              </h2>
              <p className="body-lg text-gray-400">
                {t('missionText')}
              </p>
            </motion.div>
          </div>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              { icon: Mountain, title: language === 'it' ? 'Sviluppo Falesie' : 'Crag Development', desc: language === 'it' ? 'Chiodatura di nuove vie e manutenzione delle esistenti' : 'Bolting new routes and maintaining existing ones' },
              { icon: Calendar, title: language === 'it' ? 'Eventi & Community' : 'Events & Community', desc: language === 'it' ? 'Organizzazione di raduni, corsi e attività sociali' : 'Organizing meetups, courses and social activities' },
              { icon: Heart, title: language === 'it' ? 'Promozione Territorio' : 'Territory Promotion', desc: language === 'it' ? 'Valorizzazione del patrimonio naturale siciliano' : 'Promoting the Sicilian natural heritage' },
            ].map((item, i) => (
              <motion.div 
                key={i}
                variants={itemVariants}
                className="bg-white/5 border border-white/10 p-8 rounded-lg text-center hover:border-[#FF6D00]/50 transition-colors"
              >
                <item.icon className="w-10 h-10 text-[#FF6D00] mx-auto mb-4" />
                <h3 className="font-heading text-xl text-white uppercase mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Crags Preview */}
      <section className="section-padding" data-testid="crags-preview">
        <div className="container-custom">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="caption mb-2 block">{t('crags')}</span>
              <h2 className="heading-lg text-[#0A0A0A]">Le nostre falesie</h2>
            </div>
            <Link to="/crags" className="hidden md:flex items-center text-[#FF6D00] font-semibold hover:underline">
              {t('viewAll')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {crags.slice(0, 4).map((crag, i) => (
              <motion.div
                key={crag.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={`/crags/${crag.slug}`} className="group block">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-lg">
                    <img 
                      src={crag.images?.[0] || 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=400'}
                      alt={crag.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-heading text-xl text-white uppercase mb-2">{crag.name}</h3>
                      <div className="flex items-center gap-4 text-white/80 text-sm">
                        <span>{crag.total_routes || 0} {t('routes')}</span>
                        <span>{crag.grade_range}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <Link to="/crags" className="md:hidden flex items-center justify-center mt-8 text-[#FF6D00] font-semibold">
            {t('viewAll')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </section>

      {/* Events Preview */}
      {events.length > 0 && (
        <section className="section-padding" data-testid="events-preview">
          <div className="container-custom">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="caption mb-2 block">{t('events')}</span>
                <h2 className="heading-lg text-[#0A0A0A]">{t('upcomingEvents')}</h2>
              </div>
              <Link to="/team/events" className="hidden md:flex items-center text-[#FF6D00] font-semibold hover:underline">
                {t('viewAll')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {events.map((event, i) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card-base"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-[#FF6D00] text-white p-3 rounded text-center min-w-[60px]">
                      <p className="font-heading text-2xl font-bold">
                        {new Date(event.date).getDate()}
                      </p>
                      <p className="text-xs uppercase">
                        {new Date(event.date).toLocaleDateString(language, { month: 'short' })}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg uppercase mb-2">
                        {getTranslatedContent(event.title)}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {getTranslatedContent(event.description)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Projects Preview */}
      {projects.length > 0 && (
        <section className="section-padding bg-gray-50" data-testid="projects-preview">
          <div className="container-custom">
            <div className="flex justify-between items-end mb-12">
              <div>
                <span className="caption mb-2 block">{t('projects')}</span>
                <h2 className="heading-lg text-[#0A0A0A]">I nostri progetti</h2>
              </div>
              <Link to="/team/projects" className="hidden md:flex items-center text-[#FF6D00] font-semibold hover:underline">
                {t('viewAll')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {projects.map((project, i) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="card-base"
                >
                  <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded ${
                    project.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {project.status === 'completed' ? t('completed') : t('inProgress')}
                  </span>
                  <h3 className="font-heading text-xl uppercase mt-4 mb-2">
                    {getTranslatedContent(project.title)}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3">
                    {getTranslatedContent(project.description)}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Donation CTA */}
      <section className="section-padding bg-[#FF6D00]" data-testid="donation-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-12 h-12 text-white mx-auto mb-6" />
            <h2 className="heading-lg text-white mb-6">
              {t('donationCTA')}
            </h2>
            <p className="body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              {language === 'it' 
                ? 'Il tuo contributo ci aiuta a sviluppare nuovi settori, mantenere le vie esistenti e promuovere l\'arrampicata in Sicilia.'
                : 'Your contribution helps us develop new sectors, maintain existing routes and promote climbing in Sicily.'}
            </p>
            <Link to="/donations">
              <Button className="bg-white text-[#FF6D00] hover:bg-gray-100 font-bold uppercase tracking-wider px-8 py-4" data-testid="donate-cta-btn">
                {t('donate')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
